import classNames from "classnames";
import { FC, useCallback, useRef, useState } from "react";
import { useI18n } from "../../hooks/useTranslations";
import styles from "./FileSelector.module.css";
import FormField from "./FormField";
import Input from "./Input";

const mimeToExt: Record<string, string> = {
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "text/tab-separated-values": "tsv",
  "application/vnd.oasis.opendocument.spreadsheet": "ods",
  "text/plain": "txt",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "application/pdf": "pdf",
  "application/json": "json",
  "application/zip": "zip",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/ogg": "ogg",
  "text/html": "html",
  "text/css": "css",
  "text/javascript": "js",
  "application/xml": "xml",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
};

interface FileSelectorProps {
  onFileSelected: (file: File) => void;
  isInvalid?: boolean;
  allowMultiSelection?: boolean;
  accept?: string;
  disabled?: boolean;
}

const FileSelector: FC<FileSelectorProps> = ({
  onFileSelected,
  isInvalid,
  allowMultiSelection,
  accept,
  disabled,
}: FileSelectorProps) => {
  const i18n = useI18n();

  const accepted = accept?.split(",").map((s) => s.trim().replace(/^\./, "").toLowerCase()) ?? [];

  const [isDragging, setIsDragging] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File>();
  const [isFileValid, setIsFileValid] = useState(true);

  const setFileValues = (file?: File | null) => {
    if (file) {
      setFile(file);
      onFileSelected(file);
      setIsFileValid(true);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    const f = event.target.files?.[0];
    if (!f) return;

    if (accept) {
      const mime = f.type ?? "";
      const extFromMime = mimeToExt[mime];

      const isValid = accepted?.includes(extFromMime ?? "");
      setIsFileValid(isValid);
      setFile(f);

      if (!isValid) {
        event.target.value = "";
        setFileValues(null);
        return;
      }
    }

    setFileValues(f);
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      if (disabled) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) setIsDragging(true);

      if (accept) {
        const mime = e.dataTransfer?.items[0]?.type ?? "";
        const extFromMime = mimeToExt[mime];

        const isValid = accepted?.includes(extFromMime ?? "");
        setIsFileValid(isValid);

        e.dataTransfer.dropEffect = !isValid ? "none" : "copy";
      }
    },
    [isDragging, accept, disabled, setIsFileValid, setIsDragging],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      if (disabled) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (isDragging) {
        setIsDragging(false);
        setIsFileValid(true);
      }
    },
    [disabled, isDragging],
  );

  const handleDrop = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (imageInputRef.current) {
      const file = e.dataTransfer.files?.[0];
      if (isFileValid) {
        setFileValues(file);
        imageInputRef.current.files = e.dataTransfer.files;
      }
    }
  }, [disabled, isFileValid]);

  return (
    <label onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      <FormField
        error={
          !isFileValid && accept ? { type: "pattern", message: i18n.t.common.fileTypeSupported(accepted) } : undefined
        }
      >
        <input
          type="file"
          onChange={handleFileChange}
          style={{ display: "none" }}
          ref={imageInputRef}
          accept={accept}
          disabled={disabled}
        />
        <Input
          className={classNames(styles["file-selector"])}
          value={
            !isFileValid
              ? i18n.t.common.fileNotSupported
              : isDragging
                ? i18n.t.common.dropIt
                : file?.name && !allowMultiSelection
                  ? file.name
                  : i18n.t.common.dragAndDropOrUploadAnFile
          }
          isInvalid={isInvalid || !isFileValid}
          disabled={disabled}
          readOnly
          onClick={() => {
            if (!disabled) {
              imageInputRef.current?.click();
            }
          }}
        />
      </FormField>
    </label>
  );
};

export default FileSelector;
